import {redirectTo} from 'helpers/redirect';
import URL from 'url';
import Const from 'constants';
import ConstraintsHandlerFactory from 'services/simplex/constraints-handler-factory';
import {assertUserConfigurationIsValid} from 'services/validations';
import {showPopup} from 'helpers/modal';
import {createChecksumForLineupPlayers} from 'helpers/hash';
import {createLineupPlayerMap, createLineupPlayerMapByLineupRoles} from 'helpers/lineups-map';
import {combineOptimizedAndLateSwapUsedPlayers} from 'components/organisms/used-players-list/helpers';
import {createLineupsStorage} from 'storages/lineups-storage';
import {getLineupService, getPlayerService, getLpService} from 'services/sports/factory';
import {round} from 'helpers/number-helper';
import CorrelationStackGroupsBuilder from 'components/organisms/correlation-stacking/services/correlation-stack-groups-builder';
import {createLineupCorrelationStackCombinationsMap} from 'components/organisms/correlation-stacking/helpers';
import {createLineupTeamCombinationsMap} from 'components/organisms/teams-combinations/helpers';
import {createLineupKeyPlayerStackCombinationsMap} from 'components/organisms/key-player-combinations/helpers';
import KeyPlayerStackGroupsBuilder from 'services/key-player-stacking/key-player-stack-groups-builder';
import {createLineupProjectionBoostMap} from 'components/organisms/projection-boost-rule/helpers';

let Lineup = {
    getLineups(needRedirect = true) {
        return (dispatch, getState) => {
            try {
                dispatch({type: Const.OPTIMIZATION_START});

                dispatch(this.createPoolsOfPlayers());

                const {sportConfig} = getState();

                assertUserConfigurationIsValid({sportConfig});

                // TODO creating groups of stacks should be removed
                dispatch(this.createGroupsOfStacks());

                dispatch(this.runOptimization(new ConstraintsHandlerFactory(getState()).initHandler()));
            } catch (e) {
                console.error(e);
                dispatch(
                    showPopup(e.message, {
                        onConfirm: function () {
                            dispatch({type: Const.OPTIMIZATION_TERMINATE});
                            if (needRedirect) {
                                dispatch(redirectTo(URL.to('/players')));
                            }
                        },
                    }),
                );
            }
        };
    },
    prepareLineupPlayers(params) {
        return (dispatch, getState) => {
            const {
                sportConfig,
                projectionBoostRule,
                playersData: {players: playersPool},
                lineupsData: {rawLineups},
                projectionPointsType,
            } = getState();
            const {lineupPlayers, lineupNumber, lineupId} = params;

            /** if algo generates lineup with wrong size, return empty lineup. https://alteam.atlassian.net/browse/DFSA-1226 */
            if (sportConfig.isModeLateswap() && lineupPlayers.length !== sportConfig.getPlayersInTeam()) {
                return [];
            }

            const projectionBoostMap = createLineupProjectionBoostMap(
                rawLineups.getLineup(lineupId),
                projectionBoostRule,
                playersPool,
            );

            const players = getPlayerService(sportConfig).exec(
                'preparePlayersForLineup',
                {
                    lineupPlayers,
                    lineupNumber,
                    projectionBoostMap,
                    projectionPointsType: projectionPointsType.getProjectionPointsType(),
                },
                [],
            );

            return getLineupService(sportConfig).exec('setPlayersByColumns', {team: players, lineupId}, []);
        };
    },
    fetchLineup(lineupPlayers, lineupId, lineupNumber) {
        return (dispatch) => {
            const lineup = dispatch(this.do('createLineupView', lineupPlayers, lineupNumber, lineupId));

            if (lineup === null) {
                return;
            }

            dispatch(this.setOptimizedLineupViews([lineup]));
            dispatch(this.setLineupsUsedPlayers());
        };
    },
    fetchBackendLineups(items) {
        return (dispatch, getState) => {
            const {sportConfig} = getState();

            const lineups = [];
            const PlayerService = getPlayerService(sportConfig, {store: null});
            items.map((item, index) => {
                const lineupNumber = 1 + index;
                const lineupPlayers = item
                    .getUnits()
                    .map((pId) => PlayerService.exec('getPlayerById', {
                        id: pId.replace(Const.LP_UNIT_PREFIX, ''),
                    }));
                const lineup = dispatch(this.do('createLineupView', lineupPlayers, lineupNumber, item.getId()));

                lineup !== null && lineups.push(lineup);
            });

            dispatch(this.setOptimizedLineupViews(lineups));
            dispatch(this.setLineupsUsedPlayers());
        };
    },
    fetchFrontendLineups(items) {
        return (dispatch, getState) => {
            const {sportConfig} = getState();
            const lineups = [];
            const LPService = getLpService(sportConfig, {store: null});

            items.map((item, index) => {
                const lineupNumber = 1 + index;
                const lineupPlayers = item
                    .getUnits()
                    .map((pId) => LPService.exec('preparePlayerByLPPlayerId', {
                        id: pId,
                    }));    
                const lineup = dispatch(this.do('createLineupView', lineupPlayers, lineupNumber, item.getId()));

                lineup !== null && lineups.push(lineup);
            });

            dispatch(this.setOptimizedLineupViews(lineups));
            dispatch(this.setLineupsUsedPlayers());
        };
    },
    setOptimizedLineupViews(lineups) {
        return {
            type: Const.GET_LINEUPS_SUCCESS,
            lineups,
        };
    },
    setStatusOfLineupsIsFilled(value) {
        return {
            type: Const.SET_STATUS_OF_LINEUPS_IS_FILLED,
            data: value,
        };
    },
    setRawLineup(lineup) {
        return (dispatch) => {
            dispatch(this.setSimplexLineupToCreatedLateswapLineupsMap(lineup));

            dispatch({
                type: Const.SET_RAW_LINEUP,
                lineup,
            });
        };
    },
    unsetRawLineups() {
        return {
            type: Const.UNSET_RAW_LINEUPS,
        };
    },
    setSimplexLineupToCreatedLateswapLineupsMap(lineup) {
        return (dispatch, getState) => {
            const state = getState();
            const {
                sportConfig,
                lateSwap: {createdLineupsMap, groupSameLineups},
            } = state;

            if (!sportConfig.isModeLateswap() || !groupSameLineups.isUsed()) {
                return;
            }

            const lineupsStorage = createLineupsStorage(state);

            const lineupView = lineupsStorage.getLateswapLineupViewByEntryId(lineup.getEntryId());

            if (!lineupView) {
                return;
            }

            if (lineupView.playersChecksum in createdLineupsMap.lineupsMap) {
                return;
            }

            dispatch({
                type: Const.LATESWAP_SET_CREATED_LINEUPS_MAP,
                data: {
                    ...createdLineupsMap.lineupsMap,
                    [lineupView.playersChecksum]: lineup.getId(),
                },
            });
        };
    },
    setLineupsUsedPlayers(params = {}) {
        return (dispatch, getState) => {
            const {
                sportConfig,
                playersData: {players},
                lineupsData: {lineups},
                lateSwap: {usedPlayers: lateSwapUsedPlayers},
            } = getState();
            const {lineupIdsOnly = []} = params;
            const lineupService = getLineupService(sportConfig);

            let filteredLineups = lineups.getAllAsArr();
            if (!isEmpty(lineupIdsOnly)) {
                filteredLineups = filteredLineups.filter((lineup) => inArray(lineup.lineupId, lineupIdsOnly));
            }

            let usedPlayers = dispatch(this.calcLineupsUsedPlayers(filteredLineups));

            if (sportConfig.isModeLateswap() && lateSwapUsedPlayers.hasPlayers()) {
                usedPlayers = combineOptimizedAndLateSwapUsedPlayers(usedPlayers, lateSwapUsedPlayers);
            }

            const notExcludedPlayers = lineupService.exec(
                'prepareAllNotExcludedPlayersForExposureBox',
                {
                    players: players.getAllNotExcludedAsArr(),
                    sportConfig,
                },
                [],
            );

            usedPlayers = lineupService.exec(
                'combineOptimizedAndNotExcludedPlayers',
                {
                    optimizedPlayers: usedPlayers,
                    notExcludedPlayers,
                    sportConfig,
                },
                [],
            );

            dispatch({
                type: Const.SET_LINEUPS_USED_PLAYERS,
                data: usedPlayers,
            });
        };
    },
    createLineupPlayerMap() {
        return (dispatch, getState) => {
            const {
                lineupsData: {lineups},
                sportConfig,
            } = getState();

            const shouldBeGroupedByLineupRoles =
                (sportConfig.isGameTypeShowDownOrSingleGame() && !sportConfig.isSportGolf()) ||
                (sportConfig.isSportMma() && sportConfig.isSiteFanduel()) ||
                sportConfig.isSportF1();

            const map = shouldBeGroupedByLineupRoles
                ? createLineupPlayerMapByLineupRoles(lineups)
                : createLineupPlayerMap(lineups);

            dispatch({
                type: Const.SET_LINEUP_PLAYER_MAP,
                data: map,
            });
        };
    },
    createLineupTeamCombinationsMap() {
        return (dispatch, getState) => {
            const {
                lineupsData: {rawLineups},
                sportConfig,
            } = getState();

            if (!sportConfig.isSportMlb() || !sportConfig.isGameTypeClassic()) {
                return;
            }

            const map = createLineupTeamCombinationsMap(rawLineups);

            dispatch({
                type: Const.SET_LINEUP_TEAM_COMBINATIONS_MAP,
                data: map,
            });
        };
    },
    resolveCorrelationStackCombinations() {
        return (dispatch, getState) => {
            const {
                runSettings,
                sportConfig,
                playersData: {players},
                lineupsData: {rawLineups},
                correlationStacking: {combinations, settings},
            } = getState();

            if (
                !sportConfig.isSportNfl() ||
                !sportConfig.isGameTypeClassic() ||
                !runSettings.hasRunSettingsByKey(settings.getStateId())
            ) {
                return;
            }

            const groupBuilder = new CorrelationStackGroupsBuilder({
                stacking: runSettings.getRunSettingsByKey(settings.getStateId()),
                players,
                comboExpMax: combinations.exposureMax,
            });
            const stackGroups = groupBuilder.prepareGroups(rawLineups.asArr());

            dispatch({
                type: Const.CORRELATION_STACKING_SET_COMBINATION_GROUPS,
                data: stackGroups,
            });

            dispatch({
                type: Const.SET_LINEUP_CORRELATION_STACK_COMBINATIONS_MAP,
                data: createLineupCorrelationStackCombinationsMap([
                    ...stackGroups.usedInLineups,
                    ...stackGroups.unusedInLineups,
                ]),
            });
        };
    },
    resolveKeyPlayerStackCombinations() {
        return (dispatch, getState) => {
            const {
                runSettings,
                playersData: {players},
                lineupsData: {rawLineups},
                stacks: {groups},
                keyPlayerStacking: {combinations},
            } = getState();

            if (!runSettings.hasRunSettingsByKey(groups.getStateId())) {
                return;
            }

            const groupBuilder = new KeyPlayerStackGroupsBuilder({
                stacking: runSettings.getRunSettingsByKey(groups.getStateId()),
                players,
                comboExpMax: combinations.exposureMax,
            });
            const stackGroups = groupBuilder.getGroups(rawLineups.asArr());

            dispatch({
                type: Const.KEY_PLAYER_STACKING_SET_COMBINATION_GROUPS,
                data: stackGroups,
            });

            dispatch({
                type: Const.SET_LINEUP_KEY_PLAYER_STACK_COMBINATIONS_MAP,
                data: createLineupKeyPlayerStackCombinationsMap([
                    ...stackGroups.usedInLineups,
                    ...stackGroups.unusedInLineups,
                ]),
            });
        };
    },
    unsetLineups() {
        return {
            type: Const.UNSET_LINEUPS,
        };
    },
    createLineupView(lineupPlayers, lineupNumber, lineupId) {
        return (dispatch) => {
            const players = dispatch(
                this.prepareLineupPlayers({
                    lineupPlayers,
                    lineupNumber,
                    lineupId,
                }),
            );

            if (isEmpty(players)) {
                // https://abnk.atlassian.net/browse/DFSA-433
                return null; // dispatch(this.getCreatedLateswapLineupViewIfExists(lineupId));
            }

            const lineupTotals = dispatch(this.calcLineupTotals(players));

            return {
                lineupId,
                lineupNumber,
                players,
                playersChecksum: createChecksumForLineupPlayers(players),
                checked: true,
                ...lineupTotals,
            };
        };
    },
    calcLineupTotals(players) {
        return (dispatch, getState) => {
            const {sportConfig} = getState();

            const LineupService = getLineupService(sportConfig);

            const totalSalary = LineupService.exec('sumPlayersProperty', {players, prop: 'salary'});
            const totalPoints = LineupService.exec('sumPlayersProperty', {players, prop: 'points'});
            const totalProjOwn = LineupService.exec('sumPlayersProperty', {players, prop: 'projected_ownership'});
            const totalAdminPoints = LineupService.exec('sumPlayersProperty', {players, prop: 'adminPoints'});
            const totalUserPoints = LineupService.exec('sumPlayersProperty', {players, prop: 'userPoints'});
            const totalLineupPoints = LineupService.exec('sumPlayersRoundedProperty', {
                players,
                prop: 'lineupPoints',
                decimalPlacesCount: Const.POINTS_DECIMAL_PRECISION,
            });
            const totalFinalPoints = LineupService.exec('sumPlayersProperty', {players, prop: 'fantasyPoints'});

            return {
                totalSalary,
                totalPoints: round(totalPoints, Const.POINTS_DECIMAL_PRECISION),
                totalAdminPoints: round(totalAdminPoints, Const.POINTS_DECIMAL_PRECISION),
                totalUserPoints: round(totalUserPoints, Const.POINTS_DECIMAL_PRECISION),
                totalLineupPoints: round(totalLineupPoints, Const.POINTS_DECIMAL_PRECISION),
                totalProjOwn: round(totalProjOwn, Const.POINTS_DECIMAL_PRECISION),
                totalFinalPoints: round(totalFinalPoints, Const.POINTS_DECIMAL_PRECISION),
            };
        };
    },
    calcLineupsUsedPlayers(lineups) {
        return (dispatch, getState) => {
            const {sportConfig} = getState();

            return getLineupService(sportConfig).exec('recalculateLineupsUsedPlayers', {lineups}, []);
        };
    },
    getCreatedLateswapLineupViewIfExists(lineupId) {
        return (dispatch, getState) => {
            const lineupsStorage = createLineupsStorage(getState());

            return lineupsStorage.getLateswapLineupViewByGeneratedLineupId(lineupId);
        };
    },
    tryToResolveOptimizationFailuresIfHappened() {
        return (dispatch, getState) => {
            const {
                sportConfig,
                lineupsData: {rawLineups, lineups},
                lateSwap: {simplexLineups, lineupViews},
            } = getState();

            if (!sportConfig.isModeLateswap() || !simplexLineups.hasLineups()) {
                return;
            }

            const lateswapSimplexLineupsToAdd = [];
            const resolvedLineupViews = [];

            simplexLineups.getAllAsArr().map((simplexLineup) => {
                const rawLineup = rawLineups.getLineupByEntryId(simplexLineup.getEntryId());

                if (rawLineup !== null) {
                    const lineup = lineups.getLineup(rawLineup.getId());

                    if (lineup !== null) {
                        resolvedLineupViews.push(lineup);
                        return;
                    }
                }

                const lineupView = lineupViews.getOneByLineupId(simplexLineup.getId());

                if (lineupView !== null) {
                    lateswapSimplexLineupsToAdd.push(simplexLineup);
                    resolvedLineupViews.push(lineupView);
                }
            });

            if (!isEmpty(lateswapSimplexLineupsToAdd)) {
                lateswapSimplexLineupsToAdd.map((simplexLineup) => {
                    dispatch(this.setRawLineup(simplexLineup));
                });
                dispatch(this.setOptimizedLineupViews(resolvedLineupViews));
                dispatch(this.setLineupsUsedPlayers());
            }
        };
    },
};

export default Lineup;
